/**
 * Util functions for PacePro
 */
import { DISTANCES, DISTANCES_METERS, DISTANCE_UNITS, SPLIT_TYPES } from 'reducers/pacePro/paceProCreate';

/**
 * Convert lat/lon form degrees to semicircles.
 * PacePro algorithm expects semicircles.
 */
export const degreesToSemicircles = (degrees) => {
    return degrees * 11930464.71;
};

/**
 * PacePro algorithm outputs speed as meters per millisecond, convert to kph
 */
export const metersPerMillisecondToKmph = (mms) => {
    return mms * 3600;
};

/**
 *
 * Get the distance type for a pacePro
 */
export const getDistanceType = (distance, courseId) => {
    if (courseId) {
        return DISTANCES.COURSE;
    }
    const range = 2.0; // we match distances within 2 meters of the value
    if (DISTANCES_METERS['5K'] - range < distance && distance < DISTANCES_METERS['5K'] + range) {
        return DISTANCES['5K'];
    } else if (DISTANCES_METERS['10K'] - range < distance && distance < DISTANCES_METERS['10K'] + range) {
        return DISTANCES['10K'];
    } else if (
        DISTANCES_METERS['HALF_MARATHON'] - range < distance &&
        distance < DISTANCES_METERS['HALF_MARATHON'] + range
    ) {
        return DISTANCES['HALF_MARATHON'];
    } else if (DISTANCES_METERS['MARATHON'] - range < distance && distance < DISTANCES_METERS['MARATHON'] + range) {
        return DISTANCES['MARATHON'];
    } else {
        return DISTANCES.OTHER;
    }
};

/**
 * paceSeconds - seconds per distance, so 4 min/mi would be 240 seconds
 * distance - in miles or km, match the units used in paceSeconds
 */
export const totalTimeFromPaceAndDistance = (paceSeconds, distance) => {
    const totalTime = Math.round(paceSeconds * distance);

    return totalTime;
};

export const paceIntervalsAreEqual = (paceIntervals1, paceIntervals2) => {
    if (paceIntervals1.length !== paceIntervals2.length) {
        return false;
    }

    for (let i = 0; i < paceIntervals1.length; i++) {
        const paceInterval1 = paceIntervals1[i];
        const paceInterval2 = paceIntervals2[i];
        if (!paceIntervalIsEqual(paceInterval1, paceInterval2)) {
            return false;
        }
    }

    return true;
};

const paceIntervalIsEqual = (interval1, interval2) => {
    // if the speed and distance are equal, then the rest are equal
    return interval1.splitAvgSpeed === interval2.splitAvgSpeed && interval1.splitDistance === interval2.splitDistance;
};

/**
 * We need to show the distance in the unit that the user selected for the paceband. All other distances use the account unit.
 */
export const getDistanceUnit = (totalDistance, distanceUnit) => {
    const distanceType = getDistanceType(totalDistance);

    if (distanceType === DISTANCES['5K'] || distanceType === DISTANCES['10K']) {
        return DISTANCE_UNITS.KM;
    }

    // Use the unit the user saved the paceband in, otherwise use their account unit
    if (distanceUnit) {
        return distanceUnit;
    }

    return '';
};

export const formatDistance = (totalDistance, distanceUnit, Personalizer) => {
    const distanceDisplayUnit = getDistanceUnit(totalDistance, distanceUnit);

    switch (distanceDisplayUnit) {
        case DISTANCE_UNITS.KM:
            return Personalizer.formatDistance(totalDistance * Personalizer.ConversionFactors.MetersToKm);
        case DISTANCE_UNITS.MI:
            return Personalizer.formatDistance(totalDistance * Personalizer.ConversionFactors.MetersToMiles);
        default:
            return Personalizer.personalizeDistance(totalDistance);
    }
};

export const getDistanceUnitKey = (totalDistance, distanceUnit, Personalizer) => {
    const distanceDisplayUnit = getDistanceUnit(totalDistance, distanceUnit);

    switch (distanceDisplayUnit) {
        case DISTANCE_UNITS.KM:
            return 'distance_unit_metric';
        case DISTANCE_UNITS.MI:
            return 'distance_unit_statute';
        default:
            return Personalizer.getDistanceUnitKey();
    }
};

export const getSplitDistanceUnitKey = (splits, Personalizer) => {
    switch (splits) {
        case SPLIT_TYPES.MILE:
            return 'distance_unit_statute';
        case SPLIT_TYPES.KILOMETER:
            return 'distance_unit_metric';
        case SPLIT_TYPES.ELEVATION:
        default:
            return Personalizer.getDistanceUnitKey();
    }
};

export const formatSplitDistance = (splits, splitDistance, Personalizer) => {
    switch (splits) {
        case SPLIT_TYPES.MILE:
            return Personalizer.formatDistance(splitDistance * Personalizer.ConversionFactors.MetersToMiles);
        case SPLIT_TYPES.KILOMETER:
            return Personalizer.formatDistance(splitDistance * Personalizer.ConversionFactors.MetersToKm);
        case SPLIT_TYPES.ELEVATION:
        default:
            return Personalizer.personalizeDistance(splitDistance);
    }
};

export const PACE_PRO_MAX_COURSE_DISTANCE = 241500;
export const PACE_PRO_MIN_COURSE_DISTANCE = 1500;
export const PACE_PRO_MAX_COURSE_DISTANCE_KM = 240;
export const PACE_PRO_MIN_COURSE_DISTANCE_KM = 1.5;
export const PACE_PRO_MAX_COURSE_DISTANCE_MILES = 150;
export const PACE_PRO_MIN_COURSE_DISTANCE_MILES = 1;

export const isCourseDistanceCompatibleWithPacePro = (distanceMeter) => {
    return distanceMeter >= PACE_PRO_MIN_COURSE_DISTANCE && distanceMeter <= PACE_PRO_MAX_COURSE_DISTANCE;
};
